import { useState } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { Check, X, Sparkles, Clock, Shield, Star, Ticket } from 'lucide-react';
import { Skeleton } from './primitives';
import {
    buildPaywallFeatureRows,
    formatPlanLabel,
    getPaywallPlanBlurb,
    packagesHaveAnnualPricing,
    resolvePlanPricingDisplay,
} from '../admin/Packages/planDisplayUtils';
import { isLowerTier, normalizeTierSlug } from '../services/instructorPlanTier';

/**
 * Shared pricing-tier presentation for upgrade and reactivation dialogs.
 * Card layout follows studyak-app v2 subscription picker — colored header band,
 * tier slug badge, feature checklist, and full-width CTA bar per tier.
 */

const pop = keyframes`from { transform: translateY(20px) scale(.96); opacity: 0; }`;

/** Per-tier header gradients — maps instructor slugs to mobile v2 visual language. */
export const TIER_VISUAL_THEMES = {
    starter: {
        headerStart: 'oklch(0.78 0.06 280)',
        headerEnd: 'oklch(0.68 0.08 274)',
        headerText: 'oklch(0.98 0.01 280)',
        checkBg: 'oklch(0.55 0.21 274)',
        ctaBg: 'oklch(0.55 0.21 274)',
        ctaText: '#fff',
    },
    professional: {
        headerStart: 'oklch(0.62 0.14 247)',
        headerEnd: 'oklch(0.52 0.17 247)',
        headerText: '#fff',
        checkBg: 'oklch(0.58 0.16 247)',
        ctaBg: 'oklch(0.58 0.16 247)',
        ctaText: '#fff',
    },
    growth: {
        headerStart: 'oklch(0.58 0.20 274)',
        headerEnd: 'oklch(0.48 0.22 274)',
        headerText: '#fff',
        checkBg: 'oklch(0.55 0.21 274)',
        ctaBg: 'oklch(0.55 0.21 274)',
        ctaText: '#fff',
    },
    organization: {
        headerStart: 'oklch(0.58 0.22 305)',
        headerEnd: 'oklch(0.48 0.24 305)',
        headerText: '#fff',
        checkBg: 'oklch(0.56 0.25 305)',
        ctaBg: 'oklch(0.56 0.25 305)',
        ctaText: '#fff',
    },
    enterprise: {
        headerStart: 'oklch(0.78 0.14 66)',
        headerEnd: 'oklch(0.68 0.16 56)',
        headerText: 'oklch(0.25 0.05 66)',
        checkBg: 'oklch(0.72 0.16 66)',
        ctaBg: 'oklch(0.72 0.16 66)',
        ctaText: 'oklch(0.25 0.05 66)',
    },
};

/** Resolves tier-specific colors with accent fallback for unknown slugs. */
export function getTierVisualTheme(tierSlug, theme) {
    const slug = normalizeTierSlug(tierSlug);
    if (TIER_VISUAL_THEMES[slug]) {
        return TIER_VISUAL_THEMES[slug];
    }
    return {
        headerStart: theme.accent,
        headerEnd: theme.accentStrong,
        headerText: theme.onAccent,
        checkBg: theme.accent,
        ctaBg: theme.accent,
        ctaText: theme.onAccent,
    };
}

export const Sheet = styled.div`
    font-family: ${({ theme }) => theme.fontHead};
    background: ${({ theme }) => theme.bg};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rXl};
    box-shadow: ${({ theme }) => theme.shadowLg};
    padding: 24px clamp(20px, 3vw, 40px) 20px;
    animation: ${pop} .35s cubic-bezier(.34, 1.3, .5, 1);
    position: relative;
    max-height: min(90vh, 820px);
    overflow-y: auto;
    overflow-x: hidden;
`;

export const CloseWrap = styled.div`
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 2;

    .MuiIconButton-root {
        color: ${({ theme }) => theme.textMuted};
        &:hover {
            color: ${({ theme }) => theme.text};
            background: ${({ theme }) => theme.surface3};
        }
    }
`;

export const Head = styled.div`
    text-align: center;
    max-width: 640px;
    margin: 0 auto 18px;
`;

export const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px 5px 10px;
    border-radius: ${({ theme }) => theme.rPill};
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    margin-bottom: 12px;

    ${({ $expired, theme }) => {
        const pulse = keyframes`50% { box-shadow: 0 0 0 6px color-mix(in oklch, ${theme.warning} 12%, transparent); }`;
        return $expired
            ? css`
                background: color-mix(in oklch, ${theme.warning} 16%, ${theme.surface});
                color: oklch(0.58 0.13 60);
                border: 1px solid color-mix(in oklch, ${theme.warning} 32%, transparent);
                .dot {
                    width: 7px; height: 7px; border-radius: 50%;
                    background: oklch(0.65 0.16 60);
                    box-shadow: 0 0 0 3px color-mix(in oklch, ${theme.warning} 22%, transparent);
                    animation: ${pulse} 1.8s ease-in-out infinite;
                }
              `
            : css`
                background: ${theme.accentSoft};
                color: ${theme.accentStrong};
                border: 1px solid ${theme.accentLine};
                padding: 6px 14px;
              `;
    }}
`;

export const Title = styled.h1`
    font-family: ${({ theme }) => theme.fontHead};
    font-weight: 740;
    font-size: clamp(20px, 2.6vw, 26px);
    letter-spacing: -.02em;
    line-height: 1.2;
    margin: 0 0 8px;
    color: ${({ theme }) => theme.text};
    text-wrap: balance;
`;

export const Sub = styled.p`
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.textMuted};
    margin: 0 auto;
    max-width: 52ch;
    text-wrap: pretty;
    b { color: ${({ theme }) => theme.text}; font-weight: 650; }
`;

export const BillingToggleWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
`;

export const BillingToggle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border-radius: ${({ theme }) => theme.rPill};
    background: ${({ theme }) => theme.surface3};
    border: 1px solid ${({ theme }) => theme.border};
`;

export const BillingOption = styled.button`
    border: none;
    cursor: pointer;
    padding: 8px 20px;
    border-radius: ${({ theme }) => theme.rPill};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 13px;
    font-weight: 700;
    transition: background .15s, color .15s, box-shadow .15s;

    ${({ $active, theme }) => $active
        ? css`
            background: ${theme.surface};
            color: ${theme.text};
            box-shadow: ${theme.shadowSm};
        `
        : css`
            background: transparent;
            color: ${theme.textMuted};
            &:hover { color: ${theme.text}; }
        `}
`;

export const TiersScrollSection = styled.div`
    margin: 0 calc(-1 * clamp(20px, 3vw, 40px) + 12px);
    padding: 0 clamp(12px, 2vw, 20px);
`;

export const ScrollHint = styled.p`
    margin: -16px 0 20px;
    text-align: center;
    font-size: ${({ theme }) => theme.fsMicro};
    color: ${({ theme }) => theme.textSubtle};
    font-weight: 600;
`;

export const Grid = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-bottom: 20px;
    padding: 4px 4px 12px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.borderStrong} transparent;

    &::-webkit-scrollbar {
        height: 8px;
    }
    &::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.surface3};
        border-radius: 4px;
        margin: 0 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.borderStrong};
        border-radius: 4px;
    }
`;

export const Plan = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: 260px;
    min-width: 260px;
    max-width: 260px;
    max-height: 400px;
    scroll-snap-align: start;
    border-radius: 14px;
    overflow: hidden;
    background: ${({ theme }) => theme.surface};
    border: 1.5px solid ${({ theme }) => theme.border};
    box-shadow: ${({ theme }) => theme.shadowSm};
    transition: transform .16s, box-shadow .16s, border-color .16s, opacity .16s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => theme.shadowMd};
    }

    ${({ $hi, theme }) => $hi && css`
        border-color: ${theme.accent};
        box-shadow: 0 0 0 3px ${theme.accentSoft}, ${theme.shadowMd};
    `}

    ${({ $dimmed, theme }) => $dimmed && css`
        opacity: 0.65;

        &:hover {
            transform: none;
            box-shadow: ${theme.shadowSm};
        }
    `}
`;

export const CurrentBanner = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    right: 12px;
    z-index: 2;
    padding: 6px 12px;
    border-radius: ${({ theme }) => theme.rSm};
    background: color-mix(in oklch, ${({ theme }) => theme.success} 18%, ${({ theme }) => theme.surface});
    border: 1px solid color-mix(in oklch, ${({ theme }) => theme.success} 35%, transparent);
    color: ${({ theme }) => theme.success};
    font-size: 12px;
    font-weight: 700;
    text-align: center;
`;

export const PlanHeader = styled.div`
    padding: 14px 14px 12px;
    flex-shrink: 0;
    background: linear-gradient(
        135deg,
        ${({ $start }) => $start} 0%,
        ${({ $end }) => $end} 100%
    );
    color: ${({ $textColor }) => $textColor};
    position: relative;
`;

export const TierBadge = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: ${({ theme }) => theme.rPill};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: color-mix(in oklch, currentColor 12%, transparent);
    border: 1px solid color-mix(in oklch, currentColor 25%, transparent);
    margin-bottom: 8px;
`;

export const PopBadge = styled.div`
    position: absolute;
    top: 14px;
    right: 14px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: ${({ theme }) => theme.rPill};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background: color-mix(in oklch, #fff 22%, transparent);
    color: inherit;
    backdrop-filter: blur(4px);
`;

export const Blurb = styled.div`
    font-size: 11.5px;
    line-height: 1.4;
    opacity: 0.92;
    margin-bottom: 8px;
    text-wrap: pretty;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px;
    font-family: ${({ theme }) => theme.fontHead};

    .amt {
        font-weight: 800;
        font-size: ${({ $compact }) => ($compact ? '20px' : '26px')};
        letter-spacing: -.03em;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
    }
    .per {
        font-size: 12px;
        font-weight: 600;
        opacity: 0.85;
    }
`;

export const AnnualRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
    font-size: 12.5px;
    opacity: 0.9;

    .save {
        padding: 2px 8px;
        border-radius: ${({ theme }) => theme.rPill};
        background: color-mix(in oklch, #fff 20%, transparent);
        font-weight: 700;
        font-size: 11px;
    }
`;

export const PlanBody = styled.div`
    padding: 12px 14px 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const Feats = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0 2px 0 0;
    display: grid;
    gap: 7px;
    flex: 1;
    min-height: 0;
    max-height: 148px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.border} transparent;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 2px;
    }

    li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 12px;
        color: ${({ theme }) => theme.textMuted};
        line-height: 1.35;
    }
    li.off {
        color: ${({ theme }) => theme.textSubtle};
        text-decoration: line-through;
        opacity: 0.7;
    }
    li.more {
        color: ${({ theme }) => theme.text};
        font-weight: 600;
    }
`;

export const CheckMark = styled.span`
    display: grid;
    place-items: center;
    flex: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: color-mix(in oklch, ${({ $color }) => $color} 14%, transparent);
    color: ${({ $color }) => $color};

    &.off {
        background: ${({ theme }) => theme.surface3};
        color: ${({ theme }) => theme.textSubtle};
    }
`;

export const CtaWrap = styled.div`
    padding: 12px 14px 14px;
    margin-top: auto;
    flex-shrink: 0;
`;

export const Cta = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 44px;
    width: 100%;
    padding: 0 14px;
    border-radius: 10px;
    border: none;
    font-family: ${({ theme }) => theme.fontHead};
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -.005em;
    cursor: pointer;
    transition: opacity .15s, transform .08s;

    &:active:not(:disabled) { transform: translateY(1px); }
    &:disabled { opacity: .65; cursor: not-allowed; }

    ${({ $bg, $fg }) => css`
        background: ${$bg};
        color: ${$fg};
    `}

    ${({ $variant, theme }) => $variant === 'ghost' && css`
        background: ${theme.surface2};
        color: ${theme.text};
        border: 1.5px solid ${theme.borderStrong};
    `}

    .cta-price {
        font-size: 13px;
        font-weight: 700;
        opacity: 0.95;
        white-space: nowrap;
    }
    .cta-sub {
        font-size: 11px;
        font-weight: 600;
        opacity: 0.8;
        display: block;
        margin-top: 2px;
    }
`;

export const Trust = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 12.5px;
    color: ${({ theme }) => theme.textMuted};
    padding: 18px 0 6px;
    border-top: 1px solid ${({ theme }) => theme.border};
    span { display: inline-flex; align-items: center; gap: 6px; }
    svg { color: ${({ theme }) => theme.success}; }
    .dot { width: 3px; height: 3px; border-radius: 50%; background: ${({ theme }) => theme.textSubtle}; }
`;

export const Foot = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding-top: 16px;
    @media (max-width: 880px) { flex-wrap: wrap; }
`;

export const FootLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: ${({ theme }) => theme.textMuted};
    font-size: 13px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: ${({ theme }) => theme.rSm};
    cursor: pointer;
    transition: background .12s, color .12s;
    &:hover {
        color: ${({ theme }) => theme.text};
        background: ${({ theme }) => theme.surface3};
    }
`;

/** Prominent activation-code entry — placed below plan header, above tier grid. */
export const ActivationCodeEntryBar = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    max-width: 440px;
    margin: 0 auto 22px;
    padding: 12px 18px;
    border-radius: ${({ theme }) => theme.rMd};
    border: 1.5px dashed ${({ theme }) => theme.accentLine};
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
    text-align: left;
    transition: background .15s, border-color .15s, box-shadow .15s;

    svg {
        flex: none;
        color: ${({ theme }) => theme.accentStrong};
    }

    strong {
        color: ${({ theme }) => theme.accentStrong};
        font-weight: 750;
    }

    &:hover {
        border-color: ${({ theme }) => theme.accent};
        background: color-mix(in oklch, ${({ theme }) => theme.accent} 10%, ${({ theme }) => theme.surface});
        box-shadow: 0 0 0 3px ${({ theme }) => theme.accentSoft};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 2px;
    }
`;

/**
 * Visible activation-code CTA for upgrade/reactivation dialogs — mirrors mobile v2 sheet entry.
 */
export function ActivationCodeEntryLink({ onClick }) {
    return (
        <ActivationCodeEntryBar type="button" onClick={onClick}>
            <Ticket size={17} strokeWidth={2.1} />
            <span>Have an activation code? <strong>Redeem here</strong></span>
        </ActivationCodeEntryBar>
    );
}

export const EmptyState = styled.div`
    text-align: center;
    padding: 48px 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: 15px;
`;

export const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 64px 0;
`;

/** @deprecated Use useTheme().accent in components instead. */
export const PLAN_TIER_ACCENT = '#5b54e6';

export const planTierDialogPaperProps = {
    sx: {
        width: '100%',
        maxWidth: 'min(960px, calc(100vw - 24px))',
        m: 1.5,
        background: 'transparent',
        boxShadow: 'none',
        overflow: 'hidden',
    },
};

/** Narrower sheet for activation-code redeem — same backdrop/styling as tier dialogs. */
export const activationCodeDialogPaperProps = {
    sx: {
        ...planTierDialogPaperProps.sx,
        maxWidth: 'min(480px, calc(100vw - 24px))',
    },
};

/** Theme-aware MUI backdrop for tier dialogs — matches portal Scrim opacity. */
export function getPlanTierDialogBackdropProps(theme) {
    return {
        sx: {
            backgroundColor: `color-mix(in oklch, ${theme.bg} 55%, transparent)`,
            backdropFilter: 'blur(8px)',
        },
    };
}

/** @deprecated Use getPlanTierDialogBackdropProps(theme) for dark-mode support. */
export const planTierDialogBackdropProps = getPlanTierDialogBackdropProps({
    bg: 'oklch(0.17 0.015 268)',
});

/** Parse localStorage plan id safely for numeric comparisons. */
export function parseCurrentPlanId(rawPlanId) {
    if (rawPlanId === null || rawPlanId === undefined) {
        return null;
    }
    const parsed = Number(rawPlanId);
    return Number.isNaN(parsed) ? null : parsed;
}

/** Median-priced visible plan — display-only "Most popular" highlight. */
export function getRecommendedPlanId(packages) {
    if (!packages.length) {
        return null;
    }
    const sortedByPrice = [...packages].sort((a, b) => Number(a.price) - Number(b.price));
    return sortedByPrice[Math.floor((sortedByPrice.length - 1) / 2)].planId;
}

/** CTA button variant — accent fill on recommended tier, ghost otherwise. */
export function getPlanCtaVariant(isHighlighted, pricingMode) {
    if (pricingMode === 'contact' || pricingMode === 'free') {
        return 'ghost';
    }
    return isHighlighted ? 'accent' : 'ghost';
}

/** Maps entitlement rows to feature-line copy (`5 courses`, `50 learners`). */
export function formatPlanFeatureLabel(row) {
    if (row.kind === 'metric') {
        return `${row.value} ${row.label.toLowerCase()}`;
    }
    if (row.kind === 'more') {
        return row.label;
    }
    return row.label;
}

/** @deprecated Legacy flat package rows — prefer buildPaywallFeatureRows(entitlements). */
export function buildPlanFeatures(pkg) {
    const rows = buildPaywallFeatureRows(pkg?.entitlements, 'instructor');
    if (rows.length) {
        return rows.map((row) => ({
            lbl: formatPlanFeatureLabel(row),
            on: row.on !== false,
            kind: row.kind,
        }));
    }
    const inApp = String(pkg.inAppPurchase).toLowerCase() === 'yes';
    return [
        { lbl: `${pkg.instructors} instructor seat${Number(pkg.instructors) > 1 ? 's' : ''}`, on: true, kind: 'feature' },
        { lbl: `Up to ${pkg.students} learners`, on: true, kind: 'feature' },
        { lbl: `${pkg.courses} courses`, on: true, kind: 'feature' },
        { lbl: `${pkg.assessments} assessments`, on: true, kind: 'feature' },
        { lbl: 'In-app assessment purchase', on: inApp, kind: 'feature' },
    ];
}

/** Blurb copy beneath the plan name. */
export function getPlanBlurb(pkg) {
    return getPaywallPlanBlurb(pkg, 'instructor');
}

/** Formats tier slug as uppercase badge label (ORGANIZATION). */
export function formatTierBadgeLabel(tier) {
    return String(tier || 'plan').trim().replace(/[\s-]+/g, '_').toUpperCase();
}

/** Shimmer placeholder while paywall tiers load — mirrors mobile v2 card shape. */
export function PlanTierGridShimmer({ count = 5 }) {
    const cards = Math.min(count, 5);
    return (
        <TiersScrollSection aria-hidden="true">
            <Grid>
                {Array.from({ length: cards }).map((_, index) => (
                    <Plan key={index} style={{ pointerEvents: 'none' }}>
                        <Skeleton $w="100%" $h="96px" $r="0" $mb="0" />
                        <PlanBody>
                            <Skeleton $w="85%" $h="11px" $r="6px" $mb="8px" />
                            <Skeleton $w="75%" $h="11px" $r="6px" $mb="8px" />
                            <Skeleton $w="80%" $h="11px" $r="6px" $mb="8px" />
                        </PlanBody>
                        <CtaWrap>
                            <Skeleton $w="100%" $h="44px" $r="10px" />
                        </CtaWrap>
                    </Plan>
                ))}
            </Grid>
        </TiersScrollSection>
    );
}

/**
 * Monthly / Yearly segmented control — shown when any tier has annual pricing.
 */
export function PlanBillingPeriodToggle({ billingPeriod, onChange }) {
    return (
        <BillingToggleWrap>
            <BillingToggle role="group" aria-label="Billing period">
                <BillingOption
                    type="button"
                    $active={billingPeriod === 'monthly'}
                    onClick={() => onChange('monthly')}
                >
                    Monthly
                </BillingOption>
                <BillingOption
                    type="button"
                    $active={billingPeriod === 'yearly'}
                    onClick={() => onChange('yearly')}
                >
                    Yearly
                </BillingOption>
            </BillingToggle>
        </BillingToggleWrap>
    );
}

/**
 * Renders paywall tier cards for upgrade and reactivation dialogs.
 * Layout follows studyak-app v2 subscription picker (colored header + feature body).
 */
export function PlanTierGrid({
    packages,
    recommendedId,
    buttonLoadingIndex,
    onSelectPlan,
    getCtaLabel,
    badgeLabel = 'Most popular',
    currentTier = null,
    topBadgeLabel = '★ TOP TIER',
    disableCurrentTier = true,
    disablePastTiers = true,
}) {
    const [billingPeriod, setBillingPeriod] = useState('yearly');
    const showBillingToggle = packagesHaveAnnualPricing(packages);
    const effectivePeriod = showBillingToggle ? billingPeriod : 'monthly';

    return (
        <>
            {showBillingToggle && (
                <PlanBillingPeriodToggle
                    billingPeriod={billingPeriod}
                    onChange={setBillingPeriod}
                />
            )}
            <TiersScrollSection>
                {packages.length > 3 && (
                    <ScrollHint>Scroll horizontally to compare all plans</ScrollHint>
                )}
                <Grid role="list" aria-label="Available plans">
                    {packages.map((pkg, index) => (
                        <PlanTierCard
                            key={pkg.tier || pkg.planId}
                            pkg={pkg}
                            index={index}
                            recommendedId={recommendedId}
                            buttonLoadingIndex={buttonLoadingIndex}
                            onSelectPlan={onSelectPlan}
                            getCtaLabel={getCtaLabel}
                            badgeLabel={badgeLabel}
                            topBadgeLabel={topBadgeLabel}
                            currentTier={currentTier}
                            billingPeriod={effectivePeriod}
                            disableCurrentTier={disableCurrentTier}
                            disablePastTiers={disablePastTiers}
                        />
                    ))}
                </Grid>
            </TiersScrollSection>
        </>
    );
}

/** Single mobile-style tier card — extracted to keep PlanTierGrid readable. */
function PlanTierCard({
    pkg,
    index,
    recommendedId,
    buttonLoadingIndex,
    onSelectPlan,
    getCtaLabel,
    badgeLabel,
    topBadgeLabel,
    currentTier,
    billingPeriod,
    disableCurrentTier,
    disablePastTiers,
}) {
    const theme = useTheme();
    const pricing = resolvePlanPricingDisplay(pkg, { billingPeriod });
    const isHighlighted = pkg.planId === recommendedId;
    const isCurrent = currentTier && normalizeTierSlug(pkg.tier) === normalizeTierSlug(currentTier);
    const isPastTier = disablePastTiers && isLowerTier(currentTier, pkg.tier);
    const variant = getPlanCtaVariant(isHighlighted, pricing.mode);
    const isPicking = buttonLoadingIndex === index;
    const isCtaDisabled = isPicking
        || (disableCurrentTier && isCurrent)
        || isPastTier;
    const planLabel = formatPlanLabel(pkg.name || pkg.tier);
    const tierTheme = getTierVisualTheme(pkg.tier, theme);
    const blurb = getPlanBlurb(pkg);
    const featureRows = buildPaywallFeatureRows(pkg.entitlements, 'instructor', 4);
    const feats = featureRows.length
        ? featureRows.map((row) => ({
            lbl: formatPlanFeatureLabel(row),
            on: row.on !== false,
            kind: row.kind,
        }))
        : buildPlanFeatures(pkg);
    const ctaLabel = getCtaLabel
        ? getCtaLabel(pkg, { pricingMode: pricing.mode, isHighlighted })
        : (pricing.mode === 'contact'
            ? 'Contact sales'
            : pricing.mode === 'free'
                ? `Continue on ${planLabel}`
                : `Choose ${planLabel}`);
    const isTopTier = normalizeTierSlug(pkg.tier) === 'enterprise';

    const resolvedCtaLabel = disableCurrentTier && isCurrent
        ? 'Current plan'
        : isPastTier
            ? 'Included in your plan'
            : ctaLabel;

    return (
        <Plan $hi={isHighlighted} $dimmed={isPastTier}>
            {disableCurrentTier && isCurrent && (
                <CurrentBanner>You&apos;re already on this plan</CurrentBanner>
            )}
            <PlanHeader
                $start={tierTheme.headerStart}
                $end={tierTheme.headerEnd}
                $textColor={tierTheme.headerText}
            >
                {isHighlighted && (
                    <PopBadge><Sparkles size={10} />{badgeLabel}</PopBadge>
                )}
                {isTopTier && !isHighlighted && (
                    <PopBadge><Star size={10} fill="currentColor" />{topBadgeLabel}</PopBadge>
                )}
                <TierBadge>{formatTierBadgeLabel(pkg.tier || planLabel)}</TierBadge>
                <Blurb>{blurb}</Blurb>
                <PriceRow $compact={pricing.mode === 'contact'}>
                    <span className="amt">{pricing.amountLabel}</span>
                    {pricing.periodLabel && (
                        <span className="per">{pricing.periodLabel}</span>
                    )}
                </PriceRow>
                {pricing.annualLabel && (
                    <AnnualRow>
                        <span>{pricing.annualLabel}</span>
                        {pricing.savingsLabel && (
                            <span className="save">{pricing.savingsLabel}</span>
                        )}
                    </AnnualRow>
                )}
                {pricing.subLabel && !pricing.annualLabel && (
                    <AnnualRow><span>{pricing.subLabel}</span></AnnualRow>
                )}
            </PlanHeader>
            <PlanBody>
                <Feats>
                    {feats.map((feature, featureIndex) => (
                        <li
                            key={featureIndex}
                            className={[
                                feature.on ? '' : 'off',
                                feature.kind === 'more' ? 'more' : '',
                            ].filter(Boolean).join(' ')}
                        >
                            <CheckMark
                                $color={tierTheme.checkBg}
                                className={feature.on ? '' : 'off'}
                            >
                                {feature.on
                                    ? <Check size={11} strokeWidth={2.5} />
                                    : <X size={11} strokeWidth={2.5} />}
                            </CheckMark>
                            <span>{feature.lbl}</span>
                        </li>
                    ))}
                </Feats>
            </PlanBody>
            <CtaWrap>
                <Cta
                    $variant={variant}
                    $bg={variant === 'accent' ? tierTheme.ctaBg : undefined}
                    $fg={variant === 'accent' ? tierTheme.ctaText : undefined}
                    onClick={() => onSelectPlan(pkg, index, { billingPeriod: pricing.billingPeriod })}
                    disabled={isCtaDisabled}
                >
                    <span>
                        {isPicking
                            ? <><Clock size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{pricing.mode === 'contact' ? 'Opening email…' : 'Loading checkout…'}</>
                            : resolvedCtaLabel}
                        {pricing.mode === 'free' && pricing.subLabel && (
                            <span className="cta-sub">{pricing.subLabel}</span>
                        )}
                    </span>
                    {pricing.ctaPriceLabel && !isPicking && (
                        <span className="cta-price">{pricing.ctaPriceLabel}</span>
                    )}
                </Cta>
            </CtaWrap>
        </Plan>
    );
}

/** Trust strip shown below the tier grid. */
export function PlanTierTrustStrip() {
    return (
        <Trust>
            <span><Shield size={13} />Secured by Stripe</span>
            <span className="dot" />
            <span><Check size={13} />Cancel anytime</span>
        </Trust>
    );
}
